import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
    AfterViewInit,
    Component,
    computed,
    forwardRef,
    Injector,
    input,
    OnChanges,
    OnInit,
    signal,
    SimpleChanges,
    TemplateRef,
    ViewEncapsulation,
} from '@angular/core';
import { Button } from '../button/button.component';
import { Icon } from '../icon/icon.component';
import { RequiredFieldLabelDirective } from '../../directives/required-field-label/required-field-label.directive';
import { TableItem } from '../table/table.component';
import {
    ControlValueAccessor,
    FormsModule,
    NG_VALUE_ACCESSOR,
    NgControl,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { dualListItem } from './dual-list-selector.animation';

/** Segmento de texto para realce do termo buscado. */
export type DualListHighlightSegment = { text: string; match: boolean };

/**
 * Contexto passado ao template de item (via `[itemTemplate]`).
 *
 * - `$implicit`/`item`: o item exibido na linha.
 * - `list`: a qual painel a linha pertence.
 * - `highlight`: segmentos do nome com o trecho buscado marcado (para reproduzir
 *   o realce da busca no template customizado, se desejado).
 */
export type DualListItemContext = {
    $implicit: DualListSelectorItem;
    item: DualListSelectorItem;
    list: 'available' | 'selected';
    highlight: DualListHighlightSegment[];
};

/**
 * Item que deve ser exibido no seletor de lista dupla.
 */
export type DualListSelectorItem = {
    id?: string;
    name: string;
    value?: any;
    data?: any;
    visible?: boolean;
};

export type DualListSelectorFilter = {
    query: string;
    list: 'available' | 'selected' | 'both';
    /**
     * Quando fornecido, substitui o filtro textual client-side na lista de
     * selecionados. Útil para busca server-side: o chamador passa um predicate
     * baseado nos resultados do backend em vez de aplicar `includes` em memória.
     */
    selectedFilter?: ((item: DualListSelectorItem) => boolean) | null;
};

/**
 * Componente que exibe uma lista dupla de itens, permitindo a seleção e movimentação entre duas listas.
 * É utilizado para selecionar itens de uma lista de disponíveis para uma lista de selecionados.
 */
@Component({
    selector: 'ai-dual-list-selector',
    templateUrl: './dual-list-selector.component.html',
    styleUrls: ['./dual-list-selector.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    animations: [dualListItem],
    imports: [
        NgClass,
        NgTemplateOutlet,
        Button,
        Icon,
        RequiredFieldLabelDirective,
        FormsModule,
        ReactiveFormsModule,
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DualListSelector),
            multi: true,
        },
    ],
})
export class DualListSelector
    implements ControlValueAccessor, AfterViewInit, OnInit, OnChanges {
    private onChanged: (value: DualListSelectorItem[]) => void = () => { };
    private onTouched: () => void = () => { };

    // Filtro que deve ser aplicado
    public filter = input<DualListSelectorFilter | null>(null);

    // O texto que deve ser exibido acima da lista de itens disponíveis.
    public labelAvailable = input<string>('Itens disponíveis à seleção');

    // O texto que deve ser exibido acima da lista de itens selecionados.
    public labelSelected = input<string>('Itens selecionados');

    /** Itens disponíveis para seleção. */
    public availableItems = input<DualListSelectorItem[]>([]);

    /** Nome exibido como cabeçalho de cada lista. */
    public headerName = input<string>('Header Name');

    /** Texto do estado vazio "todos os itens foram selecionados" (disponíveis). */
    public readonly allSelectedText = input<string>('Todos estão selecionados');

    /** Texto do estado vazio "nenhum item foi informado" (ambas as listas). */
    public readonly noItemsText = input<string>('Nenhum item para exibir');

    /** Texto do estado vazio "nada selecionado ainda" (selecionados). */
    public readonly selectHintText = input<string>('Selecione na lista ao lado');

    /**
     * Template opcional para customizar cada linha das listas. O contexto
     * recebido é um {@link DualListItemContext}. Sem ele, é usado o template
     * padrão (nome do item com realce do termo buscado).
     */
    public readonly itemTemplate = input<TemplateRef<DualListItemContext> | null>(
        null,
    );

    /** Quantidade de itens na lista de disponíveis (exibida no cabeçalho). */
    protected readonly availableCount = computed(
        () => this.availableItemsTable().length,
    );

    /** Quantidade de itens na lista de selecionados (exibida no cabeçalho). */
    protected readonly selectedCount = computed(
        () => this.selectedItemsTable().length,
    );

    /**
     * Motivo pelo qual a lista de DISPONÍVEIS está vazia (ou `null` quando há
     * itens visíveis): `noItems` (nenhum item informado / nada a exibir) ou
     * `allSelected` (todos os itens foram movidos para selecionados).
     */
    protected readonly availableEmpty = computed<'allSelected' | 'noItems' | null>(
        () => {
            if (this.visibleAvailableItems().length > 0) {
                return null;
            }
            const total =
                this.availableItemsTable().length +
                this.selectedItemsTable().length;
            if (total === 0) {
                return 'noItems';
            }
            return this.availableItemsTable().length === 0
                ? 'allSelected'
                : 'noItems';
        },
    );

    /**
     * Motivo pelo qual a lista de SELECIONADOS está vazia (ou `null` quando há
     * itens visíveis): `noItems` (nenhum item informado) ou `selectNone` (há
     * itens disponíveis, mas nada foi selecionado ainda).
     */
    protected readonly selectedEmpty = computed<'selectNone' | 'noItems' | null>(
        () => {
            if (this.visibleSelectedItems().length > 0) {
                return null;
            }
            const total =
                this.availableItemsTable().length +
                this.selectedItemsTable().length;
            if (total === 0) {
                return 'noItems';
            }
            return this.selectedItemsTable().length === 0
                ? 'selectNone'
                : 'noItems';
        },
    );

    // Itens que estão na tabela de disponíveis para seleção.
    protected availableItemsTable = signal<TableItem[][]>([]);

    // Itens visíveis que estão na tabela de disponíveis.
    protected visibleAvailableItems = signal<TableItem[][]>([]);

    // Itens que estão na tabela de selecionados.
    protected selectedItemsTable = signal<TableItem[][]>([]);

    // Itens visíveis que estão na tabela de selecionados.
    protected visibleSelectedItems = signal<TableItem[][]>([]);

    // Indica se o campo é obrigatório.
    // Obtido a partir dos validators do formControl.
    protected required = signal<boolean>(false);

    // Desabilita o componente via binding `[disabled]`.
    protected readonly disabledInput = input<boolean>(false, {
        alias: 'disabled',
    });
    // Desabilitação vinda do Form (CVA, via `setDisabledState`).
    private readonly cvaDisabled = signal<boolean>(false);
    // Estado efetivo: desabilitado por input OU pelo Form.
    protected readonly disabled = computed(
        () => this.disabledInput() || this.cvaDisabled(),
    );

    // Índice do item ativo (navegação por teclado) em cada painel.
    protected activeAvailableIndex = signal<number>(-1);
    protected activeSelectedIndex = signal<number>(-1);

    /** Combina marcas diacríticas (acentos) — faixa Unicode U+0300–U+036F. */
    private static readonly DIACRITICS = new RegExp('[\\u0300-\\u036f]', 'g');

    constructor(private injector: Injector) { }

    /**
     * Implementação do Hook OnChanges.
     * Reagir a mudanças dos itens disponíveis passados via Input.
     *
     * @param changes Mudanças detectadas nos inputs do componente.
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (changes['availableItems']) {
            this.handleAvailableItems(
                this.selectedItemsTable().map(
                    (row) => row[0].value.item as DualListSelectorItem,
                ),
            );
            const currentFilter = this.filter();
            if (currentFilter && currentFilter.query && currentFilter.query.isNotEmpty()) {
                this.applyFilter(currentFilter);
            } else {
                this.updateVisibleItems();
            }
        }

        if (changes['filter']) {
            const filter: DualListSelectorFilter =
                changes['filter'].currentValue;
            this.applyFilter(filter);
        }
    }

    /**
     * Aplica o filtro aos itens disponíveis e selecionados, se houver um filtro definido.
     *
     * @param filter Filtro a ser aplicado aos itens disponíveis e selecionados.
     * @private
     */
    private applyFilter(filter: DualListSelectorFilter): void {
        if (filter && filter.query && filter.query.isNotEmpty()) {
            this.applyFilterToAvailableItems(filter);
            this.applyFilterToSelectedItems(filter);
            this.updateVisibleItems();
        } else {
            this.clearFilters();
            this.updateVisibleItems();
        }
    }

    /**
     * Implementação do Hook OnInit.
     * Inicializa as tabelas de itens disponíveis e selecionados.
     */
    ngOnInit(): void {
        this.availableItemsTable.set(
            this.transformDualListItemToTableItem(
                this.availableItems(),
                'available',
            ),
        );

        this.selectedItemsTable.set([]);

        // Sincroniza as listas visíveis com as tabelas recém-inicializadas.
        // Sem isto, as referências exibidas (vindas do ngOnChanges, que roda
        // antes do ngOnInit) divergem das tabelas, e o primeiro clique/seleção
        // não se reflete na tela.
        this.updateVisibleItems();
    }

    /**
     * Implementação do Hook AfterViewInit.
     *
     * Utilizado para definir o valor do campo required
     * a partir do NgControl do formControl associado.
     *
     * Não é possível injetar diretamente o NgControl, pois dá
     * erro de referência circular.
     */
    ngAfterViewInit(): void {
        queueMicrotask(() => {
            // `optional`: o componente pode ser usado fora de um formulário
            // (ex.: Storybook, uso só com `[availableItems]`). Sem isto,
            // `injector.get(NgControl)` lançaria "No provider for NgControl".
            const ngControl = this.injector.get(NgControl, null, {
                optional: true,
            });

            this.required.set(
                ngControl?.control?.hasValidator(Validators.required) ?? false,
            );
        });
    }

    /**
     * Implementação do método writeValue do ControlValueAccessor.
     *
     * Coloca os itens na lista de itens selecionados e remove da
     * lista de itens disponíveis.
     *
     * @param items Lista de itens a serem exibidos na tabela de itens selecionados.
     */
    public writeValue(items: DualListSelectorItem[]): void {
        this.handleAvailableItems(items);
        // Sincroniza as listas visíveis com as tabelas recém-atualizadas para
        // que a exibição (e o estado vazio) reflita o valor escrito.
        this.updateVisibleItems();
    }

    /**
     * Implementação do método registerOnChange do ControlValueAccessor.
     * Registra a função que deve ser chamada quando os itens selecionados mudarem.
     *
     * @param fn Função que deve ser chamada quando os itens selecionados mudarem.
     */
    registerOnChange(fn: any): void {
        this.onChanged = fn;
    }

    /**
     * Implementação do método registerOnTouched do ControlValueAccessor.
     * Registra a função que deve ser chamada quando o componente for tocado.
     *
     * @param fn Função que deve ser chamada quando o componente for tocado.
     */
    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    /**
     * Implementação do método setDisabledState do ControlValueAccessor.
     * Desabilita ou habilita o componente de acordo com o valor de isDisabled.
     *
     * @param isDisabled Indica se o componente deve ser desabilitado.
     */
    setDisabledState(isDisabled: boolean) {
        this.cvaDisabled.set(isDisabled);
    }

    /**
     * Define quais são os itens que devem ser exibidos na tabela de disponíveis a partir
     * de uma lista de itens que devem estar na tabela de selecionados.
     *
     * Basicamente, remove da tabela de disponíveis os itens que estão em selectedItems
     * e cria os itens da tabela de disponíveis a partir dos itens que sobraram.
     *
     * @param selectedItems Lista de itens selecionados.
     */
    private handleAvailableItems(selectedItems: DualListSelectorItem[]): void {
        const newAvailableItems = this.availableItems().difference(
            selectedItems,
            (a, b) => a.id === b.id,
        );

        this.availableItemsTable.set(
            this.transformDualListItemToTableItem(
                newAvailableItems,
                'available',
            ),
        );

        this.selectedItemsTable.set(
            this.transformDualListItemToTableItem(selectedItems, 'selected'),
        );
    }

    /**
     * Transforma os itens que devem ser exibidos no seletor em
     * itens de tabela (TableItem).
     *
     * @param items Lista de itens a serem transformados.
     * @param table Nome da tabela (available ou selected).
     *
     * @return Lista de itens formatados para tabela.
     */
    transformDualListItemToTableItem(
        items: DualListSelectorItem[],
        table: string,
    ): TableItem[][] {
        return items.map((item) => [
            {
                id: item.id,
                name: item.name,
                value: {
                    table: table,
                    isSelected: false,
                    item: item,
                    visible: true,
                },
            } as TableItem,
        ]);
    }

    /**
     * Marca na tela o item selecionado em uma das duas tabelas.
     *
     * Qual a tabela a ser marcada é definida pelo Item, que contém
     * a qual tabela ele pertence.
     *
     * @param item Item que foi selecionado.
     *
     */
    onSelectItem(item: TableItem): void {
        const table =
            item.value.table === 'available'
                ? this.availableItemsTable
                : this.selectedItemsTable;

        table.set(
            table().map((row) => {
                return row.map((i) => {
                    if (i.id === item.id) {
                        i.value.isSelected = !i.value.isSelected;
                    }

                    return i;
                });
            }),
        );
    }

    /**
     * Trata o clique no botão para mover itens da tabela de selecionados para disponíveis.
     */
    onClickLeftArrow(): void {
        const selectedItems = this.selectedItemsTable().filter(
            (item) => item[0].value.isSelected,
        );
        this.changeItemsTable(selectedItems, 'available');
    }

    /**
     * Trata o clique no botão para mover itens da tabela de disponíveis para selecionados.
     */
    onClickRightArrow(): void {
        const availableItems = this.availableItemsTable().filter(
            (item) => item[0].value.isSelected,
        );
        this.changeItemsTable(availableItems, 'selected');
    }

    /**
     * Trata o clique nos botões de seta dupla para mover todos os itens
     * da tabela de selecionados para a tabela de disponíveis.
     */
    onClickDoubleLeftArrow(): void {
        this.changeItemsTable(this.selectedItemsTable(), 'available');
    }

    /**
     * Trata o clique nos botões de seta dupla para mover todos os itens
     * da tabela de disponíveis para a tabela de selecionados.
     */
    onClickDoubleRightArrow(): void {
        this.changeItemsTable(this.availableItemsTable(), 'selected');
    }

    /**
     * Trata o clique duplo em um item de qualquer uma das tabelas.
     *
     * @param item Item que foi clicado duas vezes.
     */
    onDoubleClickItem(item: TableItem): void {
        if (item.value.table === 'available') {
            this.changeItemsTable([[item]], 'selected');
        } else {
            this.changeItemsTable([[item]], 'available');
        }
    }

    /**
     * Trata a mudança de itens entre as tabelas de disponíveis e selecionados.
     * Move os items para a tabela de selecionados e remove-os da tabela de disponíveis.
     *
     * @param items Lista de itens a serem movidos.
     * @param targetTable Tabela de destino (available ou selected).
     */
    private changeItemsTable(
        items: TableItem[][],
        targetTable: 'available' | 'selected',
    ): void {
        items.forEach((row) => {
            row.forEach((item) => {
                item.value.isSelected = false;
                item.value.table = targetTable;

                if (targetTable === 'available') {
                    // Remover o item da tabela de selecionados.
                    this.selectedItemsTable.set(
                        this.selectedItemsTable().keep(
                            (i) => i[0].id !== item.id,
                        ),
                    );

                    // Adicionar o item na tabela de disponíveis.
                    this.availableItemsTable.set(
                        [...this.availableItemsTable(), [item]].sort(
                            (a, b) => (a[0].value.item as DualListSelectorItem).name
                                .localeCompare((b[0].value.item as DualListSelectorItem).name, 'pt-BR', { sensitivity: 'base' }),
                        ),
                    );
                } else {
                    // Remover da lista de disponíveis o item que foi movido para a lista de selecionados.
                    this.availableItemsTable.set(
                        this.availableItemsTable().keep(
                            (i) => i[0].id !== item.id,
                        ),
                    );

                    this.selectedItemsTable.set(
                        [...this.selectedItemsTable(), [item]].sort(
                            (a, b) => (a[0].value.item as DualListSelectorItem).name
                                .localeCompare((b[0].value.item as DualListSelectorItem).name, 'pt-BR', { sensitivity: 'base' }),
                        ),
                    );
                }
            });
        });

        // Atualiza o valor do ControlValueAccessor com os itens selecionados.
        const selections = this.selectedItemsTable().map((row) => {
            return row[0].value.item as DualListSelectorItem;
        });

        this.updateVisibleItems();
        this.onChanged(selections);
    }

    /**
     * Zera todos os filtros, exibindo todos os itens das duas listas.
     *
     * @private
     */
    private clearFilters(): void {
        this.selectedItemsTable.set(
            this.selectedItemsTable().map((row) => {
                row[0].value.visible = true;
                return row;
            }),
        );

        this.availableItemsTable.set(
            this.availableItemsTable().map((row) => {
                row[0].value.visible = true;
                return row;
            }),
        );
    }

    /**
     * Aplica o filtro, se houver um filtro, aos itens disponíveis.
     *
     * @param filter Filtro a ser aplicado aos itens disponíveis.
     * @private
     */
    private applyFilterToAvailableItems(filter: DualListSelectorFilter): void {
        if (filter.list === 'available' || filter.list === 'both') {
            const items = this.availableItemsTable().map((row) => {
                const item = row[0].value.item as DualListSelectorItem;
                row[0].value.visible = item.name
                    .toLowerCase()
                    .includes(filter.query.toLowerCase());
                return row;
            });

            this.availableItemsTable.set(items);
        }
    }

    /**
     * Aplica o filtro, se houver um filtro, aos itens selecionados.
     *
     * @param filter Filtro a ser aplicado aos itens selecionados.
     * @private
     */
    private applyFilterToSelectedItems(filter: DualListSelectorFilter): void {
        if (filter.list === 'selected' || filter.list === 'both') {
            const items = this.selectedItemsTable().map((row) => {
                const item = row[0].value.item as DualListSelectorItem;
                row[0].value.visible = filter.selectedFilter
                    ? filter.selectedFilter(item)
                    : item.name.toLowerCase().includes(filter.query.toLowerCase());

                return row;
            });

            this.selectedItemsTable.set(items);
        }
    }

    // Atualizar tabelas de itens visíveis
    private updateVisibleItems(): void {
        this.visibleAvailableItems.set(
            this.availableItemsTable().filter((item) => item[0].value.visible),
        );

        this.visibleSelectedItems.set(
            this.selectedItemsTable().filter((item) => item[0].value.visible),
        );
    }

    // ---- Navegação por teclado ----------------------------------------------

    /**
     * Trata a navegação por teclado em um dos painéis.
     *
     * - Setas ↑/↓: movem o destaque sobre os itens visíveis.
     * - Espaço: marca/desmarca o item ativo.
     * - Enter: move o item ativo para a outra lista.
     *
     * @param list Painel onde a tecla foi pressionada.
     * @param event Evento de teclado.
     */
    protected onPanelKeydown(
        list: 'available' | 'selected',
        event: KeyboardEvent,
    ): void {
        if (this.disabled()) {
            return;
        }

        const rows =
            list === 'available'
                ? this.visibleAvailableItems()
                : this.visibleSelectedItems();
        const total = rows.length;

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                this.moveActive(list, 1, total);
                break;
            case 'ArrowUp':
                event.preventDefault();
                this.moveActive(list, -1, total);
                break;
            case ' ': {
                event.preventDefault();
                const item = this.activeRow(list);
                if (item) {
                    this.onSelectItem(item);
                }
                break;
            }
            case 'Enter': {
                event.preventDefault();
                const item = this.activeRow(list);
                if (item) {
                    this.onDoubleClickItem(item);
                    // A lista encolheu — mantém o índice dentro dos limites.
                    this.clampActive(list);
                }
                break;
            }
        }
    }

    /** Índice ativo atual do painel. */
    protected activeIndex(list: 'available' | 'selected'): number {
        return list === 'available'
            ? this.activeAvailableIndex()
            : this.activeSelectedIndex();
    }

    private setActiveIndex(list: 'available' | 'selected', value: number): void {
        if (list === 'available') {
            this.activeAvailableIndex.set(value);
        } else {
            this.activeSelectedIndex.set(value);
        }
    }

    private moveActive(
        list: 'available' | 'selected',
        delta: number,
        total: number,
    ): void {
        if (total === 0) {
            this.setActiveIndex(list, -1);
            return;
        }
        const current = this.activeIndex(list);
        const start = current < 0 ? (delta > 0 ? 0 : total - 1) : current + delta;
        this.setActiveIndex(list, ((start % total) + total) % total);
    }

    private clampActive(list: 'available' | 'selected'): void {
        const total =
            list === 'available'
                ? this.visibleAvailableItems().length
                : this.visibleSelectedItems().length;
        const current = this.activeIndex(list);
        if (total === 0) {
            this.setActiveIndex(list, -1);
        } else if (current >= total) {
            this.setActiveIndex(list, total - 1);
        }
    }

    private activeRow(list: 'available' | 'selected'): TableItem | null {
        const rows =
            list === 'available'
                ? this.visibleAvailableItems()
                : this.visibleSelectedItems();
        const index = this.activeIndex(list);
        return index >= 0 && index < rows.length ? rows[index][0] : null;
    }

    // ---- Realce do termo buscado --------------------------------------------

    /**
     * Quebra o nome do item em segmentos, marcando o trecho que casa com a
     * busca ativa (`filter().query`), de forma case/acento-insensível.
     *
     * @param name Nome a destacar.
     * @param list Painel ao qual o item pertence (respeita o escopo do filtro).
     */
    protected highlightSegments(
        name: string,
        list: 'available' | 'selected',
    ): DualListHighlightSegment[] {
        const filter = this.filter();
        const query = (filter?.query ?? '').trim();
        const inScope =
            !!filter && (filter.list === list || filter.list === 'both');

        if (!query || !inScope) {
            return [{ text: name, match: false }];
        }

        const normName = this.normalize(name);
        const normQuery = this.normalize(query);

        // Se a normalização não preservou o comprimento (caso raro), não fatiar.
        if (!normQuery || normName.length !== name.length) {
            return [{ text: name, match: false }];
        }

        const index = normName.indexOf(normQuery);
        if (index < 0) {
            return [{ text: name, match: false }];
        }

        const end = index + normQuery.length;
        const segments: DualListHighlightSegment[] = [];
        if (index > 0) {
            segments.push({ text: name.slice(0, index), match: false });
        }
        segments.push({ text: name.slice(index, end), match: true });
        if (end < name.length) {
            segments.push({ text: name.slice(end), match: false });
        }
        return segments;
    }

    /** Normaliza texto para busca: minúsculas e sem acentos. */
    private normalize(text: string): string {
        return (text ?? '')
            .toLowerCase()
            .normalize('NFD')
            .replace(DualListSelector.DIACRITICS, '');
    }
}
